import { useNavigate, useParams, Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import {
  fetchThirdPartyRequest,
  setFormData,
  putRequest,
  updateUser,
  fetchCourseReviews,
} from "../../../../redux/action";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getTokenFromCookie } from "../../../../utils/constants";
import { FaStar } from "react-icons/fa";
import {
  CertificateIcon,
  Playvid,
  sarahJackson,
  BeginnerIcon,
  UserIcon,
  StudentsIcon,
  Uiux2,
  cancelWhiteIcon,
  Videoplay,
  completedIcon,
} from "../../../../images";
import { renderValidUrl } from "../../../../utils/constants";
import { Calendar, User, User2, User2Icon } from "lucide-react";

const LevelIcon = ({ level, className }) => {
  const normalizedLevel = (level || "Beginner").toLowerCase();
  let bars = 1;
  if (
    normalizedLevel.includes("medium") ||
    normalizedLevel.includes("intermediate")
  )
    bars = 2;
  if (
    normalizedLevel.includes("advanced") ||
    normalizedLevel.includes("expert") ||
    normalizedLevel.includes("pro")
  )
    bars = 3;

  let pathD =
    "M4.74609 10.998C5.43645 10.998 5.99609 11.5577 5.99609 12.248V16.748C5.99609 17.4384 5.43645 17.998 4.74609 17.998H1.74609C1.05574 17.998 0.496094 17.4384 0.496094 16.748V12.248C0.496094 11.5577 1.05574 10.998 1.74609 10.998H4.74609Z " +
    "M11.374 7.50684C12.0043 7.57092 12.4961 8.10285 12.4961 8.75V16.75L12.4893 16.8779C12.4294 17.4661 11.9622 17.9334 11.374 17.9932L11.2461 18H8.24609C7.59894 18 7.06702 17.5082 7.00293 16.8779L6.99609 16.75V8.75C6.99609 8.05964 7.55574 7.5 8.24609 7.5H11.2461L11.374 7.50684Z " +
    "M17.8701 1.50586C18.5004 1.56995 18.9922 2.10187 18.9922 2.74902V16.749L18.9854 16.877C18.9255 17.4651 18.4583 17.9324 17.8701 17.9922L17.7422 17.999H14.7422C14.095 17.999 13.5631 17.5072 13.499 16.877L13.4922 16.749V2.74902C13.4922 2.05867 14.0518 1.49902 14.7422 1.49902H17.7422L17.8701 1.50586Z ";

  if (bars < 2) pathD += "M8.24609 16.75H11.2461V8.75H8.24609V16.75Z ";
  if (bars < 3) pathD += "M14.7422 16.749H17.7422V2.74902H14.7422V16.749Z ";

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path fillRule="evenodd" clipRule="evenodd" d={pathD} fill="#F8D210" />
    </svg>
  );
};

export default function CourseHeroCard({
  setShowRateModal,
  section,
  course,
  fallbackPayments = [],
}) {
  // State to control modal visibility
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { formData, user, courseReviews } = useSelector((state) => state);
  const dispatch = useDispatch();
  const [userRegion, setUserRegion] = useState(formData?.defaultRegion || null);

  useEffect(() => {
    const fetchCountry = async () => {
      if (formData?.defaultRegion) {
        setUserRegion(formData.defaultRegion);
        return;
      }
      try {
        const data = await fetchThirdPartyRequest("https://ipapi.co/json/");
        if (data?.country_name) {
          const region =
            data.country_name === "United States" ||
            data.country_name === "Canada"
              ? "US & Canada"
              : "Europe - Africa";

          setUserRegion(region);
          dispatch(setFormData({ defaultRegion: region }));
        }
      } catch (err) {
        console.error("Error fetching country:", err);
      }
    };
    fetchCountry();
  }, [dispatch, formData?.defaultRegion]);

  useEffect(() => {
    if (course?.id) {
      dispatch(fetchCourseReviews(course.id));
    }
  }, [dispatch, course?.id]);

  const enrolled = useMemo(() => {
    const courses = user?.enrolledCourses || user?.enrolled_courses || [];
    const targetId = course?.id || id;
    return courses.some((c) => {
      const cid = typeof c === "object" ? c.id || c.course_id : c;
      return Number(cid) === Number(targetId);
    });
  }, [user, course, id]);

  const { averageRating, totalRatings } = useMemo(() => {
    const reviews = courseReviews?.data || [];
    if (!reviews || reviews.length === 0) {
      return { averageRating: "0.0", totalRatings: 0 };
    }
    const total = reviews.reduce((acc, curr) => {
      const rating = Number(curr.attributes?.rating || curr.rating || 0);
      return acc + rating;
    }, 0);
    return {
      averageRating: (total / reviews.length).toFixed(1),
      totalRatings: reviews.length,
    };
  }, [courseReviews?.data]);

  const courseDurationWeeks = useMemo(() => {
    if (!course?.startDate || !course?.endDate) return "8hr 30mins";
    const start = new Date(course.startDate);
    const end = new Date(course.endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "8hr 30mins";

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.round(diffDays / 7);

    return weeks > 0
      ? `${weeks} Week${weeks > 1 ? "s" : ""}`
      : "Less than a week";
  }, [course?.startDate, course?.endDate]);

  const handleFreeEnrollClick = async () => {
    if (!user) {
      toast.error("Please log in to enroll for free");
      return;
    }
    const token = getTokenFromCookie();
    const existingCourses =
      user.enrolledCourses?.map((c) => (typeof c === "object" ? c.id : c)) ||
      [];
    const courseId = Number(id);

    if (existingCourses.includes(courseId)) {
      toast.info("You are already enrolled in this course");
      return;
    }

    try {
      const updatedCourses = [...existingCourses, courseId];
      const [success, responseData] = await putRequest(
        `/api/users/${user.id}`,
        { enrolledCourses: updatedCourses },
        token,
      );

      if (!success || responseData?.error) {
        toast.error(
          responseData?.error?.message || "An error occurred while enrolling",
        );
      } else {
        const appendedCourses = [...(user.enrolledCourses || []), course];
        const fullyUpdatedUser = {
          ...user,
          ...responseData,
          enrolledCourses: appendedCourses,
        };
        dispatch(updateUser(fullyUpdatedUser));
        toast.success("Successfully enrolled in the free course!");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred. Please try again.");
    }
  };

  const category = course?.pill?.[0]?.pill1;
  const title = course?.title;
  const description = course?.description;

  const instructor =
    course?.course_instructors?.[0] || course?.course_instructor;
  const instructorImage = instructor?.image?.url
    ? renderValidUrl(instructor.image.url)
    : instructor?.photo?.url
      ? renderValidUrl(instructor.photo.url)
      : sarahJackson;
  const instructorName = instructor?.name || "Sarah Jackson";
  const instructorRole =
    instructor?.position?.[0]?.qualification || "Senior Dir Staff UX Designer";

  const videoSrc = course?.intro_video || "";

  const paymentOptions = useMemo(() => {
    const courseTypePayments = course?.course_type?.payments;
    const coursePayments = course?.payments;

    if (Array.isArray(courseTypePayments) && courseTypePayments.length > 0) {
      return courseTypePayments;
    }
    if (Array.isArray(coursePayments) && coursePayments.length > 0) {
      return coursePayments;
    }
    if (Array.isArray(fallbackPayments) && fallbackPayments.length > 0) {
      return fallbackPayments;
    }
    return [];
  }, [course?.course_type?.payments, course?.payments, fallbackPayments]);
  const payment = useMemo(
    () =>
      paymentOptions.find(
        (p) =>
          p.course_type?.type === section ||
          p.course_type?.type === "self-paced",
      ) || paymentOptions[0],
    [paymentOptions, section],
  );

  const allPriceCards = useMemo(() => {
    const scopedCards = payment?.priceCards || [];
    if (scopedCards.length > 0) return scopedCards;
    return paymentOptions.flatMap((p) => p?.priceCards || []);
  }, [payment, paymentOptions]);

  const selectedPriceCard = useMemo(() => {
    const recommendedCard = allPriceCards.find((card) =>
      (card?.topPill || "").toLowerCase().includes("recommended"),
    );
    const oneTimeCard = allPriceCards.find((card) =>
      /one[-\s]?time/i.test(card?.title || ""),
    );
    return recommendedCard || oneTimeCard || allPriceCards[0];
  }, [allPriceCards]);

  const regionPrices = useMemo(
    () => selectedPriceCard?.regionPrice || [],
    [selectedPriceCard],
  );

  const matchedPriceObj =
    regionPrices?.find((rp) => rp.region?.name === userRegion) ||
    regionPrices?.[0];

  const displayPrice = matchedPriceObj?.price ?? course?.price;
  const slashPrice =
    matchedPriceObj?.slashPrice ??
    (displayPrice != null
      ? displayPrice + (displayPrice * (course?.discountPercentage || 0)) / 100
      : null);
  const currencySymbol = matchedPriceObj?.region?.symbol || "$";

  return (
    <div className="relative w-full md:py-[40px] pt-3 px-4 md:px-10 lg:pl-[120px] bg-[white] lg:pr-[100px] flex flex-col lg:flex-row lg:gap-10 mx-auto pb-6 md:pb-0">
      {/* Left Section */}
      <div className="flex-1 w-full lg:w-1/2 h-auto opacity-100 md:space-y-3">
        <div className="flex items-center gap-2">
          <span className="bg-[#EFEFEF] text-[#333333] text-[12px] font-medium px-3 py-1 rounded-full">
            {category}
          </span>
          {course?.pill?.[0]?.pill2 && (
            <>
              <span className="w-[4px] h-[4px] rounded-full bg-[#333333]"></span>
              <span className="bg-[#EFEFEF] text-[#333333] text-[12px] font-medium px-3 py-1 rounded-full">
                {course.pill[0].pill2}
              </span>
            </>
          )}
        </div>

        <h2 className="text-[28px] mt-4 md:text-[32px] lg:text-[40px] font-bold text-[#1E1B2E]">
          {title}
        </h2>

        <p className="text-[#333333] leading[24px] font-normal text-[14px] mt-1 break-words">
          {description}
        </p>

        {/* Ratings */}
        {section !== "project" && (
          <div className="flex items-center space-x-2 mt-2">
            <span className="font-bold text-[#333333]">{averageRating}</span>
            <div className="flex text-[#FFBD41]">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} size={16} />
              ))}
            </div>
            <span className="text-[#333333] text-sm">
              ({totalRatings} ratings)
            </span>
          </div>
        )}

        {/* Course Stats */}
        {section !== "project" && (
          <div className="flex flex-col gap-3 text-[12px] text-gray-600 mt-4 md:grid md:grid-cols-2 xl:flex xl:flex-row xl:items-center xl:gap-4">
            <div className="flex items-center gap-1">
              <img
                src={StudentsIcon}
                alt="Students"
                className="w-[24px] h-[24px]"
              />{" "}
              {course?.enrolledUsers?.length || 0} student(s) enrolled
            </div>
            <div className="flex items-center gap-1">
              <img src={UserIcon} alt="Clock" className="w-[24px] h-[24px]" />{" "}
              {courseDurationWeeks}
            </div>
            <div className="flex items-center gap-1">
              <LevelIcon level={course?.level} className="w-[24px] h-[24px]" />{" "}
              {course?.level || "Beginner"}
            </div>
            <div className="flex items-center gap-1">
              <img
                src={CertificateIcon}
                alt="Certificate"
                className="w-[24px] h-[24px]"
              />{" "}
              Certificate
            </div>
          </div>
        )}
        {section === "project" && (
          <div className="flex flex-col gap-3 text-[12px] text-gray-600 mt-4 md:grid md:grid-cols-2 xl:flex xl:flex-row xl:items-center xl:gap-4">
            <div className="flex items-center gap-1">
              <Calendar className="h-6 w-6 text-primaryYellow" />4 Weeks
            </div>
            <div className="flex items-center gap-1">
              <User2 className="h-5 w-5 text-primaryYellow" />
              PO,BA
            </div>
            <div className="flex items-center gap-1">
              <img
                src={StudentsIcon}
                alt="Level"
                className="w-[24px] h-[24px]"
              />{" "}
              Group Project
            </div>
            <div className="flex items-center gap-1">
              <img
                src={UserIcon}
                alt="Certificate"
                className="w-[24px] h-[24px]"
              />{" "}
              Open
            </div>
          </div>
        )}
        {(section === "self-paced" || section === "project") && (
          <div className="flex items-center gap-2">
            {slashPrice && (
              <p className="text-[#D8D8D8] line-through ">
                {currencySymbol}
                {slashPrice}
              </p>
            )}
            <p className="text-[35px] font-bold text-secondary">
              {currencySymbol}
              {displayPrice}
            </p>
          </div>
        )}

        {/* Instructor */}
        <div className="mt-4">
          <p className="text-[#626262] text-[12px]">with:</p>
          <div className="flex items-center">
            <img
              src={instructorImage}
              alt="Instructor"
              className="w-10 h-10 rounded-full object-cover mr-3"
            />
            <div>
              <p className="font-semibold text-gray-900">{instructorName}</p>
              <p className="text-sm text-gray-500">{instructorRole}</p>
            </div>
          </div>
        </div>

        {/* Button Section */}
        {enrolled ? (
          <div className="flex flex-col lg:flex-row gap-6 my-6">
            <button className="border border-[#626262] flex gap-2 items-center justify-center rounded w-full lg:w-[50%] text-[14px] px-6 py-3 ">
              <span>
                <img src={completedIcon} alt="Enrolled icon" />
              </span>
              <span>Enrolled</span>
            </button>

            <button
              className="bg-[#F8D210] rounded w-full lg:w-[50%] font-medium text-[14px] px-6 py-3"
              onClick={() => setShowRateModal && setShowRateModal(true)}
            >
              Rate course
            </button>
          </div>
        ) : (
          <div className="mt-4">
            {section === "self-paced" ? (
              <Link
                to={`/course-details/${course?.id || id}/checkout`}
                className="w-full"
              >
                <button className="bg-[#F8D210] w-full text-[14px] rounded px-6 py-3 flex items-center justify-center gap-2">
                  Buy Now
                </button>
              </Link>
            ) : section === "project" ? (
              <Link
                to={`/project-details/${course?.id || id}/checkout`}
                className="w-full"
              >
                <button className="bg-[#F8D210] w-full text-[14px] rounded px-6 py-3 flex items-center justify-center gap-2">
                  Buy Now
                </button>
              </Link>
            ) : section === "free-course" ? (
              <button
                className="bg-[#F8D210] w-full text-[14px] rounded px-6 py-3"
                onClick={handleFreeEnrollClick}
              >
                Enroll for Free
              </button>
            ) : (
              <Link
                to={`/course-details/${course?.id || id}/checkout`}
                className="w-full"
              >
                <button className="bg-[#F8D210] w-full text-[14px] rounded px-6 py-3">
                  Enroll
                </button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Right Section (Image Preview) */}
      <div
        className={`relative w-full lg:w-1/2 flex-1 ${
          section === "self-paced" ? "h-[444px]" : "h-[375px]"
        } opacity-100 mt-6 lg:mt-0 flex-shrink-0`}
      >
        <img
          src={
            course?.thumbnail?.url
              ? renderValidUrl(course.thumbnail.url)
              : Uiux2
          }
          alt={`${title} Preview`}
          className="rounded-[4px] w-full h-full object-cover"
        />
        {course?.intro_video && (
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={() => {
                setIsVideoLoading(true);
                setIsVideoOpen(true);
              }}
              className="bg-white bg-opacity-70 rounded-full p-4 cursor-pointer hover:scale-110 transition-transform"
            >
              <img
                src={Playvid}
                alt="Play Button"
                className="w-10 h-10 object-contain"
              />
            </button>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="relative bg-[#1E1B2E] rounded-xl overflow-hidden w-[90%] max-w-3xl border border-gray-700">
            <div className="h-[480px] sm:h-[506px] relative flex items-center justify-center bg-black">
              {/* Close button */}
              <div className="absolute top-4 right-4 z-30 bg-black/50 rounded-full">
                <button
                  onClick={() => setIsVideoOpen(false)}
                  className="w-[40px] h-[40px] flex items-center justify-center hover:opacity-80 hover:bg-black/70 rounded-full transition"
                >
                  <img
                    src={cancelWhiteIcon}
                    alt="Close"
                    className="object-contain w-4 h-4"
                  />
                </button>
              </div>

              {/* Loader */}
              {isVideoLoading && (
                <div className="absolute inset-0 flex items-center justify-center z-10 flex-col gap-4">
                  <div className="w-12 h-12 border-4 border-[#333333] border-t-[#F8D210] rounded-full animate-spin"></div>
                  {/* <p className="text-white text-sm animate-pulse">Loading video...</p> */}
                </div>
              )}

              {/* Embedded Video */}
              <iframe
                width="100%"
                height="100%"
                src={videoSrc}
                title={`${title} Preview`}
                frameBorder="0"
                onLoad={() => setIsVideoLoading(false)}
                className={`transition-opacity duration-300 relative z-20 w-full h-full ${isVideoLoading ? "opacity-0" : "opacity-100"}`}
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
